import React, { useState, useEffect } from 'react';
import { Gift, ChevronLeft, ChevronRight } from 'lucide-react';
import { getRedemptions, Redemption, Pagination, isManager } from '../services/api';

const RedemptionsPage: React.FC = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadRedemptions();
  }, [page, statusFilter]);

  const loadRedemptions = async () => {
    setIsLoading(true);
    try {
      const result = await getRedemptions({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setRedemptions(result.redemptions);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Failed to load redemptions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Confirmed: 'bg-blue-100 text-blue-800',
      Fulfilled: 'bg-green-100 text-green-800',
      Pending: 'bg-yellow-100 text-yellow-800',
      Cancelled: 'bg-red-100 text-red-800',
      Expired: 'bg-gray-100 text-gray-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (!isManager()) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Gift className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-buggy-gray">Manager access required to view all redemptions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-buggy-dark">All Redemptions</h1>
        {pagination && (
          <span className="text-buggy-gray">{pagination.total} total</span>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent outline-none"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Fulfilled">Fulfilled</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Redemptions Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-buggy-gray">Loading...</div>
        ) : redemptions.length === 0 ? (
          <div className="p-8 text-center">
            <Gift className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-buggy-gray">No redemptions found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Reward</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Member</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-buggy-gray">Points</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {redemptions.map((r) => (
                    <tr key={r.redemptionId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-buggy-dark">{r.redemptionCode}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-buggy-dark">{r.rewardName}</p>
                        <p className="text-xs text-buggy-gray">{r.rewardCategory}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-buggy-dark">
                          {r.member.firstName} {r.member.lastName}
                        </p>
                        <p className="text-xs text-buggy-gray">{r.member.phoneNumber}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(r.status)}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-buggy-blue">
                        {r.pointsRedeemed}
                      </td>
                      <td className="px-4 py-3 text-buggy-gray text-sm">
                        {formatDate(r.createdDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-buggy-gray">
                  Showing {(page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RedemptionsPage;
